'use client';

import { cn } from '@/lib/utils';
import { formatCompactValue } from '@/lib/utils/number';
import { ChevronRight, Podcast } from 'lucide-react';
import type { MarketEventFeedItem, MarketEventSocialSignal } from '../ui/MarketEvent';
import { useTranslation } from '@/lib/i18n';
import { formatTimeAgo } from '@/lib/utils/date';

export default function EventCard({ event }: { event: MarketEventFeedItem }) {
  const photo =
    event.image_url ||
    event.news?.flatMap((n) => n.events).find((item) => item?.image)?.image ||
    event.relate_market?.find((item) => item?.image)?.image ||
    null;
  const { t } = useTranslation();

  return (
    <div
      className="rounded-xl w-full h-67.5 bg-cover!"
      style={photo ? { background: `url(${photo}) no-repeat center` } : undefined}
    >
      <div className="flex flex-col gap-4 p-4 bg-linear-to-r from-black/60 to-[#666]/10 rounded-xl w-full h-67.5">
        <div className="flex items-center gap-2 *:bg-black/30">
          <span className="border border-white/15 rounded-md h-5 px-2 text-xs leading-4.5 uppercase">
            {t('event')}
          </span>
          {event.category && (
            <span className="border border-white/15 rounded-md h-5 px-2 capitalize text-xs leading-4.5">
              {event.category.replace(/_/g, ' ')}
            </span>
          )}
          <span className="border border-white/15 rounded-md h-5 px-2 capitalize text-xs leading-4.5">
            {formatTimeAgo(event.latest_news_time, 'MMM D, YYYY')}
          </span>
          <div className="ml-auto flex items-center justify-center gap-1 size-5 rounded-md bg-black/15 border border-white/15 font-medium text-white">
            <ChevronRight strokeWidth={3} size={16} />
          </div>
        </div>
        <h5 className="text-[22px] leading-7 font-bold line-clamp-3">{event.title}</h5>
        <SocialEngagementCard socialSignal={event.social_signal} className="mt-auto" />
      </div>
    </div>
  );
}

export function SocialEngagementCard({
  socialSignal,
  className,
}: {
  socialSignal?: MarketEventSocialSignal;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!socialSignal) return null;

  const totalInteractions = +(socialSignal.total_interactions || 0);
  // const hasChangeIn24h = socialSignal.change_in_24h !== undefined;
  const changeIn24h = +(socialSignal.change_in_24h || totalInteractions || 0);

  // if (!totalInteractions && !changeIn24h) return null;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-[#303030] bg-black self-start',
        'px-4 py-2 flex flex-col justify-between',
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-x-3 top-0 h-px bg-linear-to-r from-transparent via-white/80 to-transparent" />
      <div className="flex items-center gap-3 text-white">
        <Podcast size={14} strokeWidth={2} />
        <span className="text-[11px] leading-5 font-medium tracking-normal uppercase">
          {t('SOCIAL ENGAGEMENT')}
        </span>
      </div>
      <div className="flex items-baseline gap-4 whitespace-nowrap mt-1.5">
        <span className="text-xl leading-none font-bold text-[#14C784]">
          {totalInteractions === 0 ? '-' : formatCompactValue(totalInteractions)}
        </span>
        <span className="text-xs leading-none font-middle text-[#757575]">
          {changeIn24h === 0 ? '-' : formatCompactValue(changeIn24h)} ({t('24h')})
        </span>
      </div>
    </div>
  );
}
